import { apiClient } from '@/services/apiClient';
import { createPortalClient } from '@/services/portalClient';

export interface SocialMediaStatus {
  facebook: {
    connected: boolean;
    pages: Array<{
      id: string;
      pageId: string;
      pageName: string;
      connectionStatus: string;
      connectedAt: string | null;
      expiresAt: string | null;
    }>;
  };
  instagram: {
    connected: boolean;
    accounts: Array<{
      id: string;
      username: string;
      accountType: string;
      connectionStatus: string;
      followersCount: number | null;
      profilePictureUrl: string | null;
    }>;
  };
  tiktok?: {
    connected: boolean;
    /** Whether posts to this connection go live (vs. to TikTok drafts). */
    directPostEnabled?: boolean;
    /** Whether the client has the auto-publish add-on (scheduled reels
     * fan out to TikTok with their standing publish options). */
    autopublishEnabled?: boolean;
    accounts: Array<{
      id: string;
      openId: string;
      displayName: string | null;
      username: string | null;
      avatarUrl: string | null;
      followersCount: number | null;
      connectionStatus: string;
      connectedAt: string | null;
    }>;
  };
  errors: Array<{
    id: number;
    platform: string;
    errorType: string;
    errorMessage: string;
    createdAt: string;
  }>;
  canProceedToApproval: boolean;
}

export interface ConnectionError {
  id: number;
  platform: string;
  errorType: string;
  errorMessage: string;
  troubleshootingSteps: string[];
  createdAt: string;
}

export const socialMediaApi = {
  /**
   * Get current social media connection status (for client portal)
   */
  async getStatus(clientId: string): Promise<SocialMediaStatus> {
    const portalClient = createPortalClient(clientId, 'client');
    return portalClient.get(`/portal/client/${clientId}/social-media/status`);
  },

  /**
   * Get current social media connection status (for admin dashboard)
   */
  async getAdminStatus(clientId: string): Promise<SocialMediaStatus> {
    return apiClient.get<SocialMediaStatus>(`/api/social-media/status/${clientId}`);
  },

  /**
   * Initiate Facebook OAuth flow
   */
  async connectFacebook(clientId: string): Promise<{ oauthUrl: string; state: string }> {
    const portalClient = createPortalClient(clientId, 'client');
    return portalClient.post(`/portal/client/${clientId}/social-media/connect/facebook`);
  },

  /**
   * Initiate TikTok OAuth flow (PKCE)
   */
  async connectTiktok(clientId: string): Promise<{ oauthUrl: string; state: string }> {
    const portalClient = createPortalClient(clientId, 'client');
    return portalClient.post(`/portal/client/${clientId}/social-media/connect/tiktok`);
  },

  /**
   * Disconnect TikTok account for a client
   */
  async disconnectTiktok(clientId: string): Promise<{ success: boolean; message?: string }> {
    return apiClient.post(`/api/social-media/disconnect/tiktok/${clientId}`);
  },

  /**
   * Turn the TikTok auto-publish add-on on or off for a client (admin).
   * Enabling records the standing publish options scheduled posts use.
   */
  async setTiktokAutopublish(
    clientId: string,
    enabled: boolean,
  ): Promise<{ success: boolean; autopublishEnabled: boolean }> {
    return apiClient.put(`/api/social-media/tiktok/autopublish/${clientId}`, {
      enabled,
    });
  },

  /**
   * Initiate a TikTok OAuth round from the admin app. The callback sends
   * the browser back to the admin client settings page.
   */
  async connectTiktokAdmin(
    clientId: string,
  ): Promise<{ success: boolean; oauthUrl: string }> {
    return apiClient.post(`/api/social-media/connect/tiktok/${clientId}`);
  },

  /**
   * Initiate a Facebook OAuth round from the admin app.
   */
  async connectFacebookAdmin(
    clientId: string,
  ): Promise<{ success: boolean; oauthUrl: string; onboarding: boolean }> {
    return apiClient.post(`/api/social-media/connect/facebook/${clientId}`);
  },

  /**
   * List the client's stored Facebook pages (admin page picker).
   */
  async getAvailablePagesAdmin(clientId: string): Promise<{
    success: boolean;
    pages: Array<{
      id: string;
      pageId: string;
      pageName: string;
      connectionStatus: string;
      instagramAccount: { id: string; username: string } | null;
    }>;
  }> {
    return apiClient.get(`/api/social-media/pages/${clientId}`);
  },

  /**
   * Pick the client's primary Facebook page (admin page picker).
   */
  async selectPageAdmin(
    clientId: string,
    facebookPageId: string,
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post(`/api/social-media/pages/select/${clientId}`, {
      facebook_page_id: facebookPageId,
    });
  },

  /**
   * Retry Instagram connection for a specific Facebook Page
   */
  async retryInstagram(
    clientId: string,
    facebookPageId: string,
  ): Promise<{ success: boolean; instagram?: any; error?: any }> {
    const portalClient = createPortalClient(clientId, 'client');
    const data = await portalClient.post(
      `/portal/client/${clientId}/social-media/connect/instagram/${facebookPageId}`
    );
    return data;
  },

  /**
   * Skip Instagram setup and proceed to approval
   */
  async skipInstagram(clientId: string): Promise<{ success: boolean }> {
    const portalClient = createPortalClient(clientId, 'client');
    return portalClient.post(`/portal/client/${clientId}/social-media/skip-instagram`);
  },

  /**
   * Get all unresolved connection errors
   */
  async getErrors(clientId: string): Promise<ConnectionError[]> {
    const portalClient = createPortalClient(clientId, 'client');
    const data = await portalClient.get<{ errors: ConnectionError[] }>(
      `/portal/client/${clientId}/social-media/errors`
    );
    return data.errors || [];
  },

  /**
   * Validate Facebook/Instagram tokens against the Meta API.
   * Updates connection_status in the database if tokens have expired.
   */
  async validateConnection(clientId: string): Promise<{
    success: boolean;
    results: Array<{
      accountId: string;
      accountName: string;
      platform: string;
      isValid: boolean;
      error: string | null;
    }>;
    error?: string;
  }> {
    return apiClient.post(`/api/social-media/validate/${clientId}`);
  },

  /**
   * Disconnect all social media accounts for a client
   * This deletes all Facebook pages and Instagram accounts from the database
   */
  async disconnect(clientId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    return apiClient.post<{ success: boolean; message?: string; error?: string }>(
      `/api/social-media/disconnect/${clientId}`
    );
  },

  /**
   * Get all Facebook pages available for selection (after OAuth)
   */
  async getAvailablePages(clientId: string): Promise<{
    success: boolean;
    pages: Array<{
      id: string;
      pageId: string;
      pageName: string;
      connectionStatus: string;
      instagramAccount: {
        id: string;
        username: string;
      } | null;
    }>;
  }> {
    const portalClient = createPortalClient(clientId, 'client');
    return portalClient.get(`/portal/client/${clientId}/social-media/pages`);
  },

  /**
   * Select which Facebook page to use for this client
   * Deletes all other pages connected to this client
   */
  async selectPage(clientId: string, facebookPageId: string): Promise<{
    success: boolean;
    message: string;
    page: {
      id: string;
      pageId: string;
      pageName: string;
    };
  }> {
    const portalClient = createPortalClient(clientId, 'client');
    return portalClient.post(`/portal/client/${clientId}/social-media/pages/select`, {
      facebook_page_id: facebookPageId,
    });
  },
};
